import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ArInvoiceListItemSchema,
  agingBasisSchema,
} from "./ar-invoices.contract";
import { EmailImportanceSchema } from "../email-drafts.contract";

export const invoiceHighlightColorSchema = z.enum(["yellow", "red", "green"]);

export type InvoiceHighlightColor = z.infer<typeof invoiceHighlightColorSchema>;

export const invoiceHighlightSchema = z.object({
  invoiceId: z.string().uuid(),
  color: invoiceHighlightColorSchema,
});

export type InvoiceHighlight = z.infer<typeof invoiceHighlightSchema>;

/**
 * Routes which mailbox a statement email is sent through.
 *
 * - `configured`: use the AR module config's outbox (Send-As alias paired with
 *   the configured mailbox UPN). Default when omitted.
 * - `user_mailbox`: send directly from a specific user UPN, with no alias.
 *   The visible From is the same as the sending mailbox.
 */
export const sendFromOverrideSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("configured") }),
  z.object({
    mode: z.literal("user_mailbox"),
    mailboxUpn: z.string().email(),
  }),
]);

export type SendFromOverride = z.infer<typeof sendFromOverrideSchema>;

export const arStatementEmailInputSchema = z.object({
  customerId: z.string().uuid(),
  recipientEmails: z.array(z.string().email()).min(1),
  minDays: z.number().int().min(0).optional(),
  maxDays: z.number().int().min(0).optional(),
  /** When true, restrict to invoices not yet due (reference date >= today).
   *  Only meaningful with basis="days_overdue". Overrides minDays/maxDays. */
  currentOnly: z.boolean().optional(),
  basis: agingBasisSchema.optional(),
  excludeInvoiceIds: z.array(z.string().uuid()).optional(),
  subjectOverride: z.string().min(1).optional(),
  htmlContentOverride: z.string().min(1).optional(),
  /** Per-send override for the PDF attachment filename. Sanitized server-side. */
  pdfFilenameOverride: z.string().min(1).max(255).optional(),
  invoiceHighlights: z.array(invoiceHighlightSchema).optional(),
  /**
   * Telemetry only: did the user actually edit the email subject or body before
   * sending? The client computes this by diffing the draft against the
   * platform-generated baseline. Because the send payload always carries the
   * subject/body as overrides (the draft is seeded from the preview), the server
   * cannot infer this from override presence — so the client reports it
   * explicitly. When omitted, the server falls back to override-presence.
   */
  editedBody: z.boolean().optional(),
  /**
   * Per-send routing for the sending mailbox. Omit to use the AR module
   * config (`configured` mode), which falls back to SendGrid when no AR
   * outbox is configured.
   */
  sendFrom: sendFromOverrideSchema.optional(),
  /** When true, BCC the sending user so they receive a copy of the statement. */
  bccSender: z.boolean().optional(),
  /** Additional BCC addresses (contacts or ad-hoc) beyond the sender. */
  bccRecipientEmails: z.array(z.string().email()).optional(),
  /** Additional manually-entered email address (persisted as a default for future sends). */
  customEmail: z.string().email().optional(),
  /** Mark the outgoing statement email's priority. Defaults to "normal". */
  importance: EmailImportanceSchema.optional(),
});

export const arStatementEmailPreviewOutputSchema = z.object({
  from: z.string(),
  to: z.string(),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  subject: z.string(),
  htmlContent: z.string(),
  pdfBase64: z.string(),
  pdfFilename: z.string(),
  invoices: z.array(ArInvoiceListItemSchema),
  templateVariables: z.record(z.string(), z.string()),
});

export type ArStatementEmailInput = z.infer<typeof arStatementEmailInputSchema>;

export type ArStatementEmailPreviewOutput = z.infer<
  typeof arStatementEmailPreviewOutputSchema
>;

export const arStatementEmailContracts = {
  preview: oc
    .input(arStatementEmailInputSchema)
    .output(arStatementEmailPreviewOutputSchema)
    .route({ method: "POST" }),

  send: oc
    .input(arStatementEmailInputSchema)
    .output(z.object({ outboundEmailId: z.string().uuid() }))
    .route({ method: "POST" }),
} as const;
